import React, { useState, useRef, useEffect } from "react";
import LoadingBar from "react-top-loading-bar";
import TopBar from "../../sections/TopBar";
import Sidebar from "../../sections/Sidebar";
import "../../customCss/ManageOfficers.css";
import DataService from "../../services/data.service";
import AuthService from "../../services/auth.service";
import { toast } from "react-toastify";
import { Modal } from "react-bootstrap";
import NotFound from "../../images/no-data-found.jpeg";
import Spinner from "../../sections/Spinner";
import {
    TextField,
    Box,
    Grid,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Typography,
} from "@mui/material";
import moment from "moment";
import * as XLSX from "xlsx";
import { Link } from "react-router-dom";

const JobApplicationsList = () => {
    const ref = useRef();
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [allApplications, setAllApplications] = useState([]);
    const [filteredApplications, setFilteredApplications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedApplication, setSelectedApplication] = useState(null);
    const [viewPopup, setViewPopup] = useState(false);
    const [statusPopup, setStatusPopup] = useState(false);
    const [deletePopup, setDeletePopup] = useState(false);
    const [btnLoading, setBtnLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [newStatus, setNewStatus] = useState("");
    const [statusNote, setStatusNote] = useState("");
    const [editPopup, setEditPopup] = useState(false);
    const [editFormData, setEditFormData] = useState({});
    const [editFiles, setEditFiles] = useState({
        photo: null,
        guardCardDocument: null,
        driverLicenseDocument: null
    });

    const truncateFileName = (name, maxLength = 20) => {
        if (!name) return "";
        if (name.length <= maxLength) return name;
        const extension = name.split(".").pop();
        const baseName = name.substring(0, maxLength - extension.length - 3);
        return `${baseName}...${extension}`;
    };

    useEffect(() => {
        document.title = "Job Applications || Admin Panel";
        window.scrollTo(0, 0);
        getAllApplications();

        const handleResize = () => {
            if (window.innerWidth < 768) setIsSidebarOpen(false);
        };
        window.addEventListener("resize", handleResize);
        handleResize();
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);

    const getAllApplications = () => {
        ref.current.continuousStart();
        setLoading(true);
        const token = AuthService.getCurrentUserTokken();
        DataService.getAll()
            .then((res) => {
                const data = res?.data?.data || [];
                setAllApplications(data);
                setFilteredApplications(data);
                ref.current.complete();
                setLoading(false);
            })
            .catch((err) => {
                console.error(err);
                toast.error("Failed to fetch applications");
                ref.current.complete();
                setLoading(false);
            });
    };

    const handleSearch = (e) => {
        const query = e.target.value.toLowerCase();
        setSearchQuery(query);
        applyFilters(query, statusFilter);
    };

    const handleStatusFilter = (e) => {
        const status = e.target.value;
        setStatusFilter(status);
        applyFilters(searchQuery, status);
    };

    const applyFilters = (query, status) => {
        let result = [...allApplications];
        if (query) {
            result = result.filter((item) =>
                item.firstName?.toLowerCase().includes(query) ||
                item.lastName?.toLowerCase().includes(query) ||
                item.email?.toLowerCase().includes(query) ||
                item.licenseNumber?.toLowerCase().includes(query)
            );
        }
        if (status) {
            result = result.filter((item) => item.status === status);
        }
        setFilteredApplications(result);
    };

    const handleView = (item) => {
        setSelectedApplication(item);
        setViewPopup(true);
    };

    const handleStatusChange = (item) => {
        setSelectedApplication(item);
        setNewStatus(item.status);
        setStatusNote("");
        setStatusPopup(true);
    };

    const submitStatusChange = () => {
        if (!selectedApplication || !newStatus) return;
        setBtnLoading(true);
        const token = AuthService.getCurrentUserTokken();
        DataService.updateStatus(selectedApplication._id, { status: newStatus, statusNote })
            .then(() => {
                toast.success(`Application ${newStatus} successfully`);
                setStatusPopup(false);
                getAllApplications();
            })
            .catch((err) => {
                const msg = err.response?.data?.message || "Failed to update status";
                toast.error(msg);
            })
            .finally(() => setBtnLoading(false));
    };

    const handleDelete = () => {
        if (!selectedApplication) return;
        setBtnLoading(true);
        DataService.remove(selectedApplication._id)
            .then(() => {
                toast.success("Application deleted successfully");
                setDeletePopup(false);
                getAllApplications();
            })
            .catch((err) => {
                const msg = err.response?.data?.message || "Failed to delete application";
                toast.error(msg);
            })
            .finally(() => setBtnLoading(false));
    };

    const handleEdit = (item) => {
        setSelectedApplication(item);
        setEditFormData({
            firstName: item.firstName || "",
            middleName: item.middleName || "",
            lastName: item.lastName || "",
            dateOfBirth: item.dateOfBirth ? moment(item.dateOfBirth).format("YYYY-MM-DD") : "",
            ssn: item.ssn || "",
            email: item.email || "",
            phone: item.phone || "",
            address: item.address || "",
            city: item.city || "",
            state: item.state || "",
            zipCode: item.zipCode || "",
            emergencyContact: item.emergencyContact || "",
            emergencyPhone: item.emergencyPhone || "",
            emergencyContactRelationship: item.emergencyContactRelationship || "",
            armedStatus: item.armedStatus || "unarmed",
            licenseNumber: item.licenseNumber || "",
            licenseExpiry: item.licenseExpiry ? moment(item.licenseExpiry).format("YYYY-MM-DD") : "",
            licenseState: item.licenseState || "",
            statusNote: item.statusNote || "",
            previousSecurityClearance: item.previousSecurityClearance || "",
            highSchoolDiploma: item.highSchoolDiploma || false,
            militaryServed: item.militaryService?.served || false,
            militaryBranch: item.militaryService?.branch || "",
            militaryRank: item.militaryService?.rank || "",
            militaryStartDate: item.militaryService?.startDate ? moment(item.militaryService.startDate).format("YYYY-MM-DD") : "",
            militaryEndDate: item.militaryService?.endDate ? moment(item.militaryService.endDate).format("YYYY-MM-DD") : "",
            hasFirearmsLicense: item.firearmsLicense?.hasLicense || false,
            firearmsLicenseNumber: item.firearmsLicense?.licenseNumber || "",
            firearmsLicenseState: item.firearmsLicense?.state || "",
            firearmsLicenseExpiry: item.firearmsLicense?.expiryDate ? moment(item.firearmsLicense.expiryDate).format("YYYY-MM-DD") : "",
            hasFirstAid: item.firstAidCertification?.hasCertification || false,
            firstAidOrg: item.firstAidCertification?.issuingOrganization || "",
            firstAidExpiry: item.firstAidCertification?.expiryDate ? moment(item.firstAidCertification.expiryDate).format("YYYY-MM-DD") : "",
            hasCPR: item.cprCertification?.hasCertification || false,
            cprOrg: item.cprCertification?.issuingOrganization || "",
            cprExpiry: item.cprCertification?.expiryDate ? moment(item.cprCertification.expiryDate).format("YYYY-MM-DD") : "",
            hasCriminalHistory: item.hasCriminalHistory || false,
            criminalHistoryDetails: item.criminalHistoryDetails || "",
            fingerprintingCompleted: item.fingerprintingCompleted || false,
            backgroundCheckAuthorized: item.backgroundCheckAuthorized || false,
            physicalLimitations: item.physicalLimitations || "",
            mentalHealthDisclosure: item.mentalHealthDisclosure || "",
            substanceAbuseDisclosure: item.substanceAbuseDisclosure || "",
            hoursPerWeek: item.availability?.hoursPerWeek || "",
            availableStartDate: item.availability?.startDate ? moment(item.availability.startDate).format("YYYY-MM-DD") : "",
        });
        setEditFiles({
            photo: null,
            guardCardDocument: null,
            driverLicenseDocument: null
        });
        setEditPopup(true);
    };

    const handleEditChange = (e) => {
        const { name, value } = e.target;
        setEditFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleEditFileChange = (e) => {
        const { name, files } = e.target;
        if (files && files[0]) {
            setEditFiles((prev) => ({ ...prev, [name]: files[0] }));
        }
    };

    const submitEdit = () => {
        if (!selectedApplication) return;
        setBtnLoading(true);

        const formData = new FormData();

        const basicFields = [
            'firstName', 'middleName', 'lastName', 'dateOfBirth', 'ssn',
            'email', 'phone', 'address', 'city', 'state', 'zipCode',
            'emergencyContact', 'emergencyPhone', 'emergencyContactRelationship',
            'armedStatus', 'licenseNumber', 'licenseExpiry', 'licenseState',
            'statusNote', 'previousSecurityClearance', 'highSchoolDiploma',
            'hasCriminalHistory', 'criminalHistoryDetails', 'fingerprintingCompleted',
            'backgroundCheckAuthorized', 'physicalLimitations', 'mentalHealthDisclosure',
            'substanceAbuseDisclosure'
        ];

        basicFields.forEach((key) => {
            if (editFormData[key] !== undefined && editFormData[key] !== '') {
                formData.append(key, editFormData[key]);
            }
        });

        const militaryService = {
            served: editFormData.militaryServed || false,
            branch: editFormData.militaryBranch || '',
            rank: editFormData.militaryRank || '',
            startDate: editFormData.militaryStartDate || null,
            endDate: editFormData.militaryEndDate || null
        };
        formData.append('militaryService', JSON.stringify(militaryService));

        const firearmsLicense = {
            hasLicense: editFormData.hasFirearmsLicense || false,
            licenseNumber: editFormData.firearmsLicenseNumber || '',
            state: editFormData.firearmsLicenseState || '',
            expiryDate: editFormData.firearmsLicenseExpiry || null
        };
        formData.append('firearmsLicense', JSON.stringify(firearmsLicense));

        const firstAidCertification = {
            hasCertification: editFormData.hasFirstAid || false,
            issuingOrganization: editFormData.firstAidOrg || '',
            expiryDate: editFormData.firstAidExpiry || null
        };
        formData.append('firstAidCertification', JSON.stringify(firstAidCertification));

        const cprCertification = {
            hasCertification: editFormData.hasCPR || false,
            issuingOrganization: editFormData.cprOrg || '',
            expiryDate: editFormData.cprExpiry || null
        };
        formData.append('cprCertification', JSON.stringify(cprCertification));

        const availability = {
            hoursPerWeek: editFormData.hoursPerWeek || '',
            startDate: editFormData.availableStartDate || null
        };
        formData.append('availability', JSON.stringify(availability));

        Object.keys(editFiles).forEach((key) => {
            if (editFiles[key]) {
                formData.append(key, editFiles[key]);
            }
        });

        DataService.update(selectedApplication._id, formData)
            .then(() => {
                toast.success("Application updated successfully");
                setEditPopup(false);
                getAllApplications();
            })
            .catch((err) => {
                const msg = err.response?.data?.message || "Failed to update application";
                toast.error(msg);
            })
            .finally(() => setBtnLoading(false));
    };

    const getStatusColor = (status) => {
        switch (status) {
            case "approved": return "success";
            case "rejected": return "error";
            default: return "warning";
        }
    };

    const exportToExcel = () => {
        if (filteredApplications.length === 0) {
            toast.error("No data available to export");
            return;
        }
        const formattedData = filteredApplications.map((item, index) => ({
            "S. No.": index + 1,
            "First Name": item.firstName || "-",
            "Middle Name": item.middleName || "-",
            "Last Name": item.lastName || "-",
            "DOB": item.dateOfBirth ? moment(item.dateOfBirth).format("ll") : "-",
            "SSN": item.ssn || "-",
            Email: item.email || "-",
            Phone: item.phone || "-",
            Address: item.address || "-",
            City: item.city || "-",
            State: item.state || "-",
            "Zip Code": item.zipCode || "-",
            "Emergency Contact": item.emergencyContact || "-",
            "Emergency Phone": item.emergencyPhone || "-",
            "Relationship": item.emergencyContactRelationship || "-",
            "Armed Status": item.armedStatus || "-",
            "License Number": item.licenseNumber || "-",
            "License Expiry": item.licenseExpiry ? moment(item.licenseExpiry).format("ll") : "-",
            "License State": item.licenseState || "-",
            "Previous Security Clearance": item.previousSecurityClearance || "-",
            "High School Diploma": item.highSchoolDiploma ? "Yes" : "No",
            "Military Service": item.militaryService?.served ? `Yes (${item.militaryService.branch})` : "No",
            "Firearms License": item.firearmsLicense?.hasLicense ? `Yes (${item.firearmsLicense.licenseNumber})` : "No",
            "First Aid Cert": item.firstAidCertification?.hasCertification ? "Yes" : "No",
            "CPR Cert": item.cprCertification?.hasCertification ? "Yes" : "No",
            "Background Check Auth": item.backgroundCheckAuthorized ? "Yes" : "No",
            "Available Date": item.availability?.startDate ? moment(item.availability.startDate).format("ll") : "Immediately",
            Status: item.status || "-",
            "Applied On": item.createdAt ? moment(item.createdAt).format("ll") : "-",
        }));

        const worksheet = XLSX.utils.json_to_sheet(formattedData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Job Applications");
        XLSX.writeFile(workbook, "Job_Applications_List.xlsx");
    };

    return (
        <>
            <LoadingBar color="#084e8a" ref={ref} height={5} />
            <div className={`main_layout ${isSidebarOpen ? "sidebar-open" : "sidebar-closed"}`}>
                <Sidebar isSidebarOpen={isSidebarOpen} />
                <div className="innerRight">
                    <TopBar title="Job Applications" menu="" toggleSidebar={toggleSidebar} />
                    <div className="officersSec">
                        <div className="row align-items-center mb-3">
                            <div className="col-sm-6 col-6">
                                <h3 className="colorHeadThree mb-0">Security Guard Applications</h3>
                            </div>
                            <div className="col-sm-6 col-6 text-right">
                                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                                    <button className="btn btn-success" onClick={exportToExcel}>
                                        <i className="far fa-file-excel me-2" />
                                        Export
                                    </button>
                                    <Link
                                        to="/job-application"
                                        className="btn btn-primary"
                                    >
                                        <i className="fas fa-plus-circle me-2" />
                                        Add New
                                    </Link>
                                </Box>
                            </div>
                        </div>

                        <Box sx={{ mb: 3, p: 2, bgcolor: "#f8f9fa", borderRadius: "8px" }}>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Search Applications"
                                        size="small"
                                        placeholder="Search by Name, Email, or License"
                                        sx={{ background: "#fff" }}
                                        value={searchQuery}
                                        onChange={handleSearch}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <FormControl fullWidth size="small" sx={{ background: "#fff" }}>
                                        <InputLabel>Filter by Status</InputLabel>
                                        <Select
                                            value={statusFilter}
                                            onChange={handleStatusFilter}
                                            label="Filter by Status"
                                        >
                                            <MenuItem value="">All</MenuItem>
                                            <MenuItem value="pending">Pending</MenuItem>
                                            <MenuItem value="approved">Approved</MenuItem>
                                            <MenuItem value="rejected">Rejected</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Box>

                        {filteredApplications.length > 0 ? (
                            <div className="tableScroller">
                                <table className="table table-striped">
                                    <thead style={{ whiteSpace: "nowrap" }}>
                                        <tr>
                                            <th scope="col">S. No.</th>
                                            <th scope="col">Name</th>
                                            <th scope="col">Email</th>
                                            <th scope="col">Phone</th>
                                            <th scope="col">Armed Status</th>
                                            <th scope="col">License No.</th>
                                            <th scope="col">Status</th>
                                            <th scope="col">Applied On</th>
                                            <th scope="col" className="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody style={{ whiteSpace: "nowrap" }}>
                                        {filteredApplications.map((item, i) => (
                                            <tr key={item._id || i}>
                                                <td>{i + 1}</td>
                                                <td>{`${item.firstName || ""} ${item.lastName || ""}`.trim() || "-"}</td>
                                                <td>{item.email || "-"}</td>
                                                <td>{item.phone || "-"}</td>
                                                <td style={{ textTransform: 'capitalize' }}>{item.armedStatus || "-"}</td>
                                                <td>{item.licenseNumber || "-"}</td>
                                                <td>
                                                    <Chip
                                                        label={item.status || "pending"}
                                                        color={getStatusColor(item.status)}
                                                        size="small"
                                                        sx={{
                                                            textTransform: 'capitalize',
                                                            '& .MuiChip-label': { color: '#fff' }
                                                        }}
                                                    />
                                                </td>
                                                <td>{item.createdAt ? moment(item.createdAt).format("ll") : "-"}</td>
                                                <td className="text-center">
                                                    <div className="tIco">
                                                        <i
                                                            className="fas fa-eye me-3"
                                                            onClick={() => handleView(item)}
                                                            title="View"
                                                            style={{ cursor: 'pointer' }}
                                                        />
                                                        <i
                                                            className="fas fa-edit me-3"
                                                            onClick={() => handleEdit(item)}
                                                            title="Edit"
                                                            style={{ cursor: 'pointer', color: '#007bff' }}
                                                        />
                                                        <i
                                                            className="fas fa-check-circle me-3"
                                                            onClick={() => handleStatusChange(item)}
                                                            title="Change Status"
                                                            style={{ cursor: 'pointer', color: '#28a745' }}
                                                        />
                                                        <i
                                                            className="fas fa-trash-alt me-3"
                                                            onClick={() => {
                                                                setSelectedApplication(item);
                                                                setDeletePopup(true);
                                                            }}
                                                            title="Delete"
                                                            style={{ cursor: 'pointer', color: '#dc3545' }}
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            !loading && (
                                <div className="text-center mt-4">
                                    <img src={NotFound} alt="noData" className="noData mb-2" />
                                    <p className="m-0">No Applications Found.</p>
                                </div>
                            )
                        )}
                    </div>
                </div>
            </div>

            <Modal show={viewPopup} onHide={() => setViewPopup(false)} centered size="lg" dialogClassName="modal-90h">
                <Modal.Header closeButton>
                    <Modal.Title>Application Details</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    {selectedApplication && (
                        <div className="row">
                            {/* Personal Information Section */}
                            <div className="col-md-12 mb-3">
                                <Typography variant="subtitle1" fontWeight="bold" sx={{ borderBottom: '1px solid #eee', mb: 2, pb: 1, color: '#1976d2' }}>
                                    Personal Information
                                </Typography>
                            </div>
                            <div className="col-md-4 mb-3">
                                <strong>First Name:</strong> {selectedApplication.firstName || "-"}
                            </div>
                            <div className="col-md-4 mb-3">
                                <strong>Middle Name:</strong> {selectedApplication.middleName || "-"}
                            </div>
                            <div className="col-md-4 mb-3">
                                <strong>Last Name:</strong> {selectedApplication.lastName || "-"}
                            </div>
                            <div className="col-md-4 mb-3">
                                <strong>Date of Birth:</strong> {selectedApplication.dateOfBirth ? moment(selectedApplication.dateOfBirth).format("ll") : "-"}
                            </div>
                            <div className="col-md-4 mb-3">
                                <strong>SSN:</strong> {selectedApplication.ssn || "-"}
                            </div>
                            <div className="col-md-4 mb-3">
                                <strong>Status:</strong> <Chip label={selectedApplication.status} color={getStatusColor(selectedApplication.status)} size="small" sx={{ textTransform: 'capitalize', '& .MuiChip-label': { color: '#fff' } }} />
                            </div>

                            {/* Contact Information Section */}
                            <div className="col-md-12 mt-3 mb-3">
                                <Typography variant="subtitle1" fontWeight="bold" sx={{ borderBottom: '1px solid #eee', mb: 2, pb: 1, color: '#1976d2' }}>
                                    Contact Information
                                </Typography>
                            </div>
                            <div className="col-md-6 mb-3">
                                <strong>Email:</strong> {selectedApplication.email || "-"}
                            </div>
                            <div className="col-md-6 mb-3">
                                <strong>Phone:</strong> {selectedApplication.phone || "-"}
                            </div>
                            <div className="col-md-12 mb-3">
                                <strong>Address:</strong> {`${selectedApplication.address || ""}, ${selectedApplication.city || ""}, ${selectedApplication.state || ""} ${selectedApplication.zipCode || ""}`.replace(/^, |, $/g, "") || "-"}
                            </div>

                            {/* Emergency Contact Section */}
                            <div className="col-md-12 mt-3 mb-3">
                                <Typography variant="subtitle1" fontWeight="bold" sx={{ borderBottom: '1px solid #eee', mb: 2, pb: 1, color: '#1976d2' }}>
                                    Emergency Contact
                                </Typography>
                            </div>
                            <div className="col-md-4 mb-3">
                                <strong>Contact Name:</strong> {selectedApplication.emergencyContact || "-"}
                            </div>
                            <div className="col-md-4 mb-3">
                                <strong>Phone:</strong> {selectedApplication.emergencyPhone || "-"}
                            </div>
                            <div className="col-md-4 mb-3">
                                <strong>Relationship:</strong> {selectedApplication.emergencyContactRelationship || "-"}
                            </div>

                            {/* License Information Section */}
                            <div className="col-md-12 mt-3 mb-3">
                                <Typography variant="subtitle1" fontWeight="bold" sx={{ borderBottom: '1px solid #eee', mb: 2, pb: 1, color: '#1976d2' }}>
                                    License Information
                                </Typography>
                            </div>
                            <div className="col-md-3 mb-3">
                                <strong>Armed Status:</strong> <span style={{ textTransform: 'capitalize' }}>{selectedApplication.armedStatus || "-"}</span>
                            </div>
                            <div className="col-md-3 mb-3">
                                <strong>License Number:</strong> {selectedApplication.licenseNumber || "-"}
                            </div>
                            <div className="col-md-3 mb-3">
                                <strong>License Expiry:</strong> {selectedApplication.licenseExpiry ? moment(selectedApplication.licenseExpiry).format("ll") : "-"}
                            </div>
                            <div className="col-md-3 mb-3">
                                <strong>License State:</strong> {selectedApplication.licenseState || "-"}
                            </div>
                            <div className="col-md-6 mb-3">
                                <strong>Security Clearance:</strong> {selectedApplication.previousSecurityClearance || "-"}
                            </div>
                            <div className="col-md-6 mb-3">
                                <strong>Applied On:</strong> {moment(selectedApplication.createdAt).format("LLL")}
                            </div>

                            {/* New Sections for View Modal */}
                            <div className="col-md-12 mt-3">
                                <Typography variant="subtitle1" fontWeight="bold" sx={{ borderBottom: '1px solid #eee', mb: 1, color: '#1976d2' }}>
                                    Employment History
                                </Typography>
                                {selectedApplication.previousEmployment && selectedApplication.previousEmployment.length > 0 ? (
                                    selectedApplication.previousEmployment.map((emp, idx) => (
                                        <Box key={idx} sx={{ mb: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                            <Typography variant="body2"><strong>{emp.jobTitle}</strong> at {emp.companyName}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {emp.startDate ? moment(emp.startDate).format("MMM YYYY") : "N/A"} - {emp.endDate ? moment(emp.endDate).format("MMM YYYY") : "Present"}
                                            </Typography>
                                            <Typography variant="caption" display="block">Reason for leaving: {emp.reasonForLeaving}</Typography>
                                        </Box>
                                    ))
                                ) : <Typography variant="body2" color="text.secondary">No employment history provided.</Typography>}
                            </div>

                            <div className="col-md-12 mt-3">
                                <Typography variant="subtitle1" fontWeight="bold" sx={{ borderBottom: '1px solid #eee', mb: 1, color: '#1976d2' }}>
                                    Education & Military
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Typography variant="body2"><strong>High School Diploma:</strong> {selectedApplication.highSchoolDiploma ? "Yes" : "No"}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="body2"><strong>Military Service:</strong> {selectedApplication.militaryService?.served ? "Yes" : "No"}</Typography>
                                        {selectedApplication.militaryService?.served && (
                                            <Box sx={{ pl: 1, mt: 0.5 }}>
                                                <Typography variant="caption" display="block">Branch: {selectedApplication.militaryService.branch || "-"}</Typography>
                                                <Typography variant="caption" display="block">Rank: {selectedApplication.militaryService.rank || "-"}</Typography>
                                                <Typography variant="caption" display="block">
                                                    Service: {selectedApplication.militaryService.startDate ? moment(selectedApplication.militaryService.startDate).format("MMM YYYY") : "-"} - {selectedApplication.militaryService.endDate ? moment(selectedApplication.militaryService.endDate).format("MMM YYYY") : "Present"}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Grid>
                                </Grid>
                                {selectedApplication.collegeEducation && selectedApplication.collegeEducation.length > 0 && (
                                    <Box mt={1}>
                                        <Typography variant="caption" fontWeight="bold">College:</Typography>
                                        {selectedApplication.collegeEducation.map((edu, idx) => (
                                            <Typography key={idx} variant="body2">• {edu.degree} from {edu.institution} ({edu.graduationYear})</Typography>
                                        ))}
                                    </Box>
                                )}
                            </div>

                            <div className="col-md-12 mt-3">
                                <Typography variant="subtitle1" fontWeight="bold" sx={{ borderBottom: '1px solid #eee', mb: 1, color: '#1976d2' }}>
                                    Certifications & Licenses
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="body2"><strong>Firearms License:</strong> {selectedApplication.firearmsLicense?.hasLicense ? "Yes" : "No"}</Typography>
                                        {selectedApplication.firearmsLicense?.hasLicense && (
                                            <Box sx={{ pl: 1, mt: 0.5 }}>
                                                <Typography variant="caption" display="block">License #: {selectedApplication.firearmsLicense.licenseNumber || "-"}</Typography>
                                                <Typography variant="caption" display="block">State: {selectedApplication.firearmsLicense.state || "-"}</Typography>
                                                <Typography variant="caption" display="block">Expiry: {selectedApplication.firearmsLicense.expiryDate ? moment(selectedApplication.firearmsLicense.expiryDate).format("ll") : "-"}</Typography>
                                            </Box>
                                        )}
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="body2"><strong>First Aid:</strong> {selectedApplication.firstAidCertification?.hasCertification ? "Yes" : "No"}</Typography>
                                        {selectedApplication.firstAidCertification?.hasCertification && (
                                            <Box sx={{ pl: 1, mt: 0.5 }}>
                                                <Typography variant="caption" display="block">Org: {selectedApplication.firstAidCertification.issuingOrganization || "-"}</Typography>
                                                <Typography variant="caption" display="block">Expiry: {selectedApplication.firstAidCertification.expiryDate ? moment(selectedApplication.firstAidCertification.expiryDate).format("ll") : "-"}</Typography>
                                            </Box>
                                        )}
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="body2"><strong>CPR:</strong> {selectedApplication.cprCertification?.hasCertification ? "Yes" : "No"}</Typography>
                                        {selectedApplication.cprCertification?.hasCertification && (
                                            <Box sx={{ pl: 1, mt: 0.5 }}>
                                                <Typography variant="caption" display="block">Org: {selectedApplication.cprCertification.issuingOrganization || "-"}</Typography>
                                                <Typography variant="caption" display="block">Expiry: {selectedApplication.cprCertification.expiryDate ? moment(selectedApplication.cprCertification.expiryDate).format("ll") : "-"}</Typography>
                                            </Box>
                                        )}
                                    </Grid>
                                </Grid>
                            </div>

                            <div className="col-md-12 mt-3">
                                <Typography variant="subtitle1" fontWeight="bold" sx={{ borderBottom: '1px solid #eee', mb: 1, color: '#1976d2' }}>
                                    Background & Health
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={4}>
                                        <Typography variant="body2"><strong>Criminal History:</strong> {selectedApplication.hasCriminalHistory ? "Yes" : "No"}</Typography>
                                        {selectedApplication.hasCriminalHistory && <Typography variant="caption" color="error">{selectedApplication.criminalHistoryDetails}</Typography>}
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Typography variant="body2"><strong>Fingerprinting:</strong> {selectedApplication.fingerprintingCompleted ? "Completed" : "Not Completed"}</Typography>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Typography variant="body2"><strong>Background Check Auth:</strong> {selectedApplication.backgroundCheckAuthorized ? "Yes" : "No"}</Typography>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Typography variant="body2"><strong>Physical Limitations:</strong> {selectedApplication.physicalLimitations || "None"}</Typography>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Typography variant="body2"><strong>Mental Health:</strong> {selectedApplication.mentalHealthDisclosure || "None"}</Typography>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Typography variant="body2"><strong>Substance Abuse:</strong> {selectedApplication.substanceAbuseDisclosure || "None"}</Typography>
                                    </Grid>
                                </Grid>
                            </div>

                            <div className="col-md-12 mt-3">
                                <Typography variant="subtitle1" fontWeight="bold" sx={{ borderBottom: '1px solid #eee', mb: 1, color: '#1976d2' }}>
                                    Availability & References
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={4}>
                                        <Typography variant="body2"><strong>Available Start:</strong> {selectedApplication.availability?.startDate ? moment(selectedApplication.availability.startDate).format("ll") : "Immediately"}</Typography>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Typography variant="body2"><strong>Hours Per Week:</strong> {selectedApplication.availability?.hoursPerWeek || "-"}</Typography>
                                    </Grid>
                                    <Grid item xs={4}>
                                        {selectedApplication.availability?.preferredShifts && (
                                            <Typography variant="body2"><strong>Shifts:</strong> {selectedApplication.availability.preferredShifts.join(", ")}</Typography>
                                        )}
                                    </Grid>
                                </Grid>

                                <Box mt={1}>
                                    <Typography variant="caption" fontWeight="bold">References:</Typography>
                                    {selectedApplication.references && selectedApplication.references.length > 0 ? (
                                        selectedApplication.references.map((ref, idx) => (
                                            <Typography key={idx} variant="body2">• {ref.name} ({ref.relationship}) - {ref.phone}</Typography>
                                        ))
                                    ) : <Typography variant="body2" color="text.secondary"> No references provided.</Typography>}
                                </Box>
                            </div>

                            <div className="col-md-12 mt-3">
                                <Typography variant="subtitle1" fontWeight="bold" sx={{ borderBottom: '1px solid #eee', mb: 2 }}>
                                    Documents
                                </Typography>
                                <Grid container spacing={2}>
                                    {selectedApplication.photo && (
                                        <Grid item xs={12} sm={4}>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                fullWidth
                                                href={selectedApplication.photo}
                                                target="_blank"
                                                startIcon={<i className="fas fa-image" />}
                                            >
                                                View Photo
                                            </Button>
                                        </Grid>
                                    )}
                                    {selectedApplication.guardCardDocument && (
                                        <Grid item xs={12} sm={4}>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                fullWidth
                                                href={selectedApplication.guardCardDocument}
                                                target="_blank"
                                                startIcon={<i className="fas fa-id-card" />}
                                            >
                                                Guard Card
                                            </Button>
                                        </Grid>
                                    )}
                                    {selectedApplication.driverLicenseDocument && (
                                        <Grid item xs={12} sm={4}>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                fullWidth
                                                href={selectedApplication.driverLicenseDocument}
                                                target="_blank"
                                                startIcon={<i className="fas fa-address-card" />}
                                            >
                                                Driver License
                                            </Button>
                                        </Grid>
                                    )}
                                    {!selectedApplication.photo && !selectedApplication.guardCardDocument && !selectedApplication.driverLicenseDocument && (
                                        <Grid item xs={12}>
                                            <Typography variant="body2" color="text.secondary">No documents uploaded.</Typography>
                                        </Grid>
                                    )}
                                </Grid>
                            </div>

                            {selectedApplication.statusNote && (
                                <div className="col-md-12 mt-3">
                                    <strong>Status Note:</strong> {selectedApplication.statusNote}
                                </div>
                            )}
                        </div>
                    )}
                </Modal.Body >
            </Modal >

            <Modal show={statusPopup} onHide={() => setStatusPopup(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Update Application Status</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    <FormControl fullWidth className="mb-3">
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={newStatus}
                            onChange={(e) => setNewStatus(e.target.value)}
                            label="Status"
                        >
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="approved">Approved</MenuItem>
                            <MenuItem value="rejected">Rejected</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField
                        fullWidth
                        label="Note (Optional)"
                        multiline
                        rows={3}
                        value={statusNote}
                        onChange={(e) => setStatusNote(e.target.value)}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={submitStatusChange}
                        disabled={btnLoading}
                    >
                        {btnLoading ? <Spinner /> : "Update Status"}
                    </button>
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setStatusPopup(false)}
                    >
                        Cancel
                    </button>
                </Modal.Footer>
            </Modal>

            {/* Delete Modal */}
            <Modal show={deletePopup} onHide={() => setDeletePopup(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Delete Application</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    <div className="text-center">
                        <p className="mb-0">
                            Are you sure you want to delete application from <strong>{selectedApplication?.firstName} {selectedApplication?.lastName}</strong>?
                        </p>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button
                        type="button"
                        className="btn btn-danger"
                        onClick={handleDelete}
                        disabled={btnLoading}
                    >
                        {btnLoading ? <Spinner /> : "Delete"}
                    </button>
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setDeletePopup(false)}
                    >
                        Cancel
                    </button>
                </Modal.Footer>
            </Modal>

            <Modal show={editPopup} onHide={() => setEditPopup(false)} centered size="lg" dialogClassName="modal-90h">
                <Modal.Header closeButton>
                    <Modal.Title>Edit Application</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="First Name"
                                name="firstName"
                                size="small"
                                value={editFormData.firstName || ""}
                                onChange={handleEditChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Middle Name"
                                name="middleName"
                                size="small"
                                value={editFormData.middleName || ""}
                                onChange={handleEditChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Last Name"
                                name="lastName"
                                size="small"
                                value={editFormData.lastName || ""}
                                onChange={handleEditChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Date of Birth"
                                name="dateOfBirth"
                                type="date"
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                value={editFormData.dateOfBirth || ""}
                                onChange={handleEditChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="SSN"
                                name="ssn"
                                size="small"
                                value={editFormData.ssn || ""}
                                onChange={handleEditChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Email"
                                name="email"
                                size="small"
                                value={editFormData.email || ""}
                                onChange={handleEditChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Phone"
                                name="phone"
                                size="small"
                                value={editFormData.phone || ""}
                                onChange={handleEditChange}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Address"
                                name="address"
                                size="small"
                                value={editFormData.address || ""}
                                onChange={handleEditChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="City"
                                name="city"
                                size="small"
                                value={editFormData.city || ""}
                                onChange={handleEditChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="State"
                                name="state"
                                size="small"
                                value={editFormData.state || ""}
                                onChange={handleEditChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Zip Code"
                                name="zipCode"
                                size="small"
                                value={editFormData.zipCode || ""}
                                onChange={handleEditChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Emergency Contact"
                                name="emergencyContact"
                                size="small"
                                value={editFormData.emergencyContact || ""}
                                onChange={handleEditChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Emergency Phone"
                                name="emergencyPhone"
                                size="small"
                                value={editFormData.emergencyPhone || ""}
                                onChange={handleEditChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Relationship"
                                name="emergencyContactRelationship"
                                size="small"
                                value={editFormData.emergencyContactRelationship || ""}
                                onChange={handleEditChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Armed Status</InputLabel>
                                <Select
                                    name="armedStatus"
                                    value={editFormData.armedStatus || "unarmed"}
                                    onChange={handleEditChange}
                                    label="Armed Status"
                                >
                                    <MenuItem value="armed">Armed</MenuItem>
                                    <MenuItem value="unarmed">Unarmed</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="License Number"
                                name="licenseNumber"
                                size="small"
                                value={editFormData.licenseNumber || ""}
                                onChange={handleEditChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="License Expiry"
                                name="licenseExpiry"
                                type="date"
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                value={editFormData.licenseExpiry || ""}
                                onChange={handleEditChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="License State"
                                name="licenseState"
                                size="small"
                                value={editFormData.licenseState || ""}
                                onChange={handleEditChange}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Status Note"
                                name="statusNote"
                                multiline
                                rows={2}
                                size="small"
                                value={editFormData.statusNote || ""}
                                onChange={handleEditChange}
                            />
                        </Grid>

                        {/* Education & Military */}
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom sx={{ mt: 2, borderBottom: '1px solid #eee', pb: 1, color: '#1976d2' }}>
                                Education & Military
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel>High School Diploma</InputLabel>
                                <Select
                                    name="highSchoolDiploma"
                                    value={editFormData.highSchoolDiploma ? "yes" : "no"}
                                    onChange={(e) => setEditFormData(prev => ({ ...prev, highSchoolDiploma: e.target.value === "yes" }))}
                                    label="High School Diploma"
                                >
                                    <MenuItem value="yes">Yes</MenuItem>
                                    <MenuItem value="no">No</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Security Clearance"
                                name="previousSecurityClearance"
                                size="small"
                                value={editFormData.previousSecurityClearance || ""}
                                onChange={handleEditChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Military Service</InputLabel>
                                <Select
                                    name="militaryServed"
                                    value={editFormData.militaryServed ? "yes" : "no"}
                                    onChange={(e) => setEditFormData(prev => ({ ...prev, militaryServed: e.target.value === "yes" }))}
                                    label="Military Service"
                                >
                                    <MenuItem value="yes">Yes</MenuItem>
                                    <MenuItem value="no">No</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        {editFormData.militaryServed && (
                            <>
                                <Grid item xs={12} sm={3}>
                                    <TextField fullWidth label="Branch" name="militaryBranch" size="small" value={editFormData.militaryBranch || ""} onChange={handleEditChange} />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <TextField fullWidth label="Rank" name="militaryRank" size="small" value={editFormData.militaryRank || ""} onChange={handleEditChange} />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <TextField fullWidth label="Start Date" name="militaryStartDate" type="date" size="small" InputLabelProps={{ shrink: true }} value={editFormData.militaryStartDate || ""} onChange={handleEditChange} />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <TextField fullWidth label="End Date" name="militaryEndDate" type="date" size="small" InputLabelProps={{ shrink: true }} value={editFormData.militaryEndDate || ""} onChange={handleEditChange} />
                                </Grid>
                            </>
                        )}

                        {/* Certifications */}
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom sx={{ mt: 2, borderBottom: '1px solid #eee', pb: 1, color: '#1976d2' }}>
                                Certifications
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Firearms License</InputLabel>
                                <Select
                                    name="hasFirearmsLicense"
                                    value={editFormData.hasFirearmsLicense ? "yes" : "no"}
                                    onChange={(e) => setEditFormData(prev => ({ ...prev, hasFirearmsLicense: e.target.value === "yes" }))}
                                    label="Firearms License"
                                >
                                    <MenuItem value="yes">Yes</MenuItem>
                                    <MenuItem value="no">No</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        {editFormData.hasFirearmsLicense && (
                            <>
                                <Grid item xs={12} sm={4}>
                                    <TextField fullWidth label="Firearms License #" name="firearmsLicenseNumber" size="small" value={editFormData.firearmsLicenseNumber || ""} onChange={handleEditChange} />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField fullWidth label="Firearms License Expiry" name="firearmsLicenseExpiry" type="date" size="small" InputLabelProps={{ shrink: true }} value={editFormData.firearmsLicenseExpiry || ""} onChange={handleEditChange} />
                                </Grid>
                            </>
                        )}
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel>First Aid Cert</InputLabel>
                                <Select
                                    name="hasFirstAid"
                                    value={editFormData.hasFirstAid ? "yes" : "no"}
                                    onChange={(e) => setEditFormData(prev => ({ ...prev, hasFirstAid: e.target.value === "yes" }))}
                                    label="First Aid Cert"
                                >
                                    <MenuItem value="yes">Yes</MenuItem>
                                    <MenuItem value="no">No</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        {editFormData.hasFirstAid && (
                            <>
                                <Grid item xs={12} sm={4}>
                                    <TextField fullWidth label="First Aid Org" name="firstAidOrg" size="small" value={editFormData.firstAidOrg || ""} onChange={handleEditChange} />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField fullWidth label="First Aid Expiry" name="firstAidExpiry" type="date" size="small" InputLabelProps={{ shrink: true }} value={editFormData.firstAidExpiry || ""} onChange={handleEditChange} />
                                </Grid>
                            </>
                        )}
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel>CPR Cert</InputLabel>
                                <Select
                                    name="hasCPR"
                                    value={editFormData.hasCPR ? "yes" : "no"}
                                    onChange={(e) => setEditFormData(prev => ({ ...prev, hasCPR: e.target.value === "yes" }))}
                                    label="CPR Cert"
                                >
                                    <MenuItem value="yes">Yes</MenuItem>
                                    <MenuItem value="no">No</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        {editFormData.hasCPR && (
                            <>
                                <Grid item xs={12} sm={4}>
                                    <TextField fullWidth label="CPR Org" name="cprOrg" size="small" value={editFormData.cprOrg || ""} onChange={handleEditChange} />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField fullWidth label="CPR Expiry" name="cprExpiry" type="date" size="small" InputLabelProps={{ shrink: true }} value={editFormData.cprExpiry || ""} onChange={handleEditChange} />
                                </Grid>
                            </>
                        )}

                        {/* Background Information */}
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom sx={{ mt: 2, borderBottom: '1px solid #eee', pb: 1, color: '#1976d2' }}>
                                Background Information
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Criminal History</InputLabel>
                                <Select
                                    name="hasCriminalHistory"
                                    value={editFormData.hasCriminalHistory ? "yes" : "no"}
                                    onChange={(e) => setEditFormData(prev => ({ ...prev, hasCriminalHistory: e.target.value === "yes" }))}
                                    label="Criminal History"
                                >
                                    <MenuItem value="yes">Yes</MenuItem>
                                    <MenuItem value="no">No</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Fingerprinting Done</InputLabel>
                                <Select
                                    name="fingerprintingCompleted"
                                    value={editFormData.fingerprintingCompleted ? "yes" : "no"}
                                    onChange={(e) => setEditFormData(prev => ({ ...prev, fingerprintingCompleted: e.target.value === "yes" }))}
                                    label="Fingerprinting Done"
                                >
                                    <MenuItem value="yes">Yes</MenuItem>
                                    <MenuItem value="no">No</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Background Check Auth</InputLabel>
                                <Select
                                    name="backgroundCheckAuthorized"
                                    value={editFormData.backgroundCheckAuthorized ? "yes" : "no"}
                                    onChange={(e) => setEditFormData(prev => ({ ...prev, backgroundCheckAuthorized: e.target.value === "yes" }))}
                                    label="Background Check Auth"
                                >
                                    <MenuItem value="yes">Yes</MenuItem>
                                    <MenuItem value="no">No</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        {editFormData.hasCriminalHistory && (
                            <Grid item xs={12}>
                                <TextField fullWidth label="Criminal History Details" name="criminalHistoryDetails" multiline rows={2} size="small" value={editFormData.criminalHistoryDetails || ""} onChange={handleEditChange} />
                            </Grid>
                        )}

                        {/* Health Disclosures */}
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom sx={{ mt: 2, borderBottom: '1px solid #eee', pb: 1, color: '#1976d2' }}>
                                Health Disclosures
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField fullWidth label="Physical Limitations" name="physicalLimitations" size="small" value={editFormData.physicalLimitations || ""} onChange={handleEditChange} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField fullWidth label="Mental Health Disclosure" name="mentalHealthDisclosure" size="small" value={editFormData.mentalHealthDisclosure || ""} onChange={handleEditChange} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField fullWidth label="Substance Abuse Disclosure" name="substanceAbuseDisclosure" size="small" value={editFormData.substanceAbuseDisclosure || ""} onChange={handleEditChange} />
                        </Grid>

                        {/* Availability */}
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom sx={{ mt: 2, borderBottom: '1px solid #eee', pb: 1, color: '#1976d2' }}>
                                Availability
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Hours Per Week" name="hoursPerWeek" size="small" value={editFormData.hoursPerWeek || ""} onChange={handleEditChange} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Available Start Date" name="availableStartDate" type="date" size="small" InputLabelProps={{ shrink: true }} value={editFormData.availableStartDate || ""} onChange={handleEditChange} />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom sx={{ mt: 2, borderBottom: '1px solid #eee', pb: 1 }}>
                                Documents
                            </Typography>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <Box>
                                <Typography variant="caption" display="block" gutterBottom>
                                    Photo {selectedApplication?.photo && <a href={selectedApplication.photo} target="_blank" rel="noreferrer">(View Current)</a>}
                                </Typography>
                                <Button variant="outlined" component="label" fullWidth size="small">
                                    {editFiles.photo ? truncateFileName(editFiles.photo.name) : "Upload New Photo"}
                                    <input type="file" hidden name="photo" accept="image/*" onChange={handleEditFileChange} />
                                </Button>
                            </Box>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <Box>
                                <Typography variant="caption" display="block" gutterBottom>
                                    Guard Card {selectedApplication?.guardCardDocument && <a href={selectedApplication.guardCardDocument} target="_blank" rel="noreferrer">(View Current)</a>}
                                </Typography>
                                <Button variant="outlined" component="label" fullWidth size="small">
                                    {editFiles.guardCardDocument ? truncateFileName(editFiles.guardCardDocument.name) : "Upload New Card"}
                                    <input type="file" hidden name="guardCardDocument" accept="image/*,.pdf" onChange={handleEditFileChange} />
                                </Button>
                            </Box>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <Box>
                                <Typography variant="caption" display="block" gutterBottom>
                                    License {selectedApplication?.driverLicenseDocument && <a href={selectedApplication.driverLicenseDocument} target="_blank" rel="noreferrer">(View Current)</a>}
                                </Typography>
                                <Button variant="outlined" component="label" fullWidth size="small">
                                    {editFiles.driverLicenseDocument ? truncateFileName(editFiles.driverLicenseDocument.name) : "Upload New License"}
                                    <input type="file" hidden name="driverLicenseDocument" accept="image/*,.pdf" onChange={handleEditFileChange} />
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Modal.Body>
                <Modal.Footer>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={submitEdit}
                        disabled={btnLoading}
                    >
                        {btnLoading ? <Spinner /> : "Save Changes"}
                    </button>
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setEditPopup(false)}
                    >
                        Cancel
                    </button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default JobApplicationsList;
